import { useId, useMemo, useState, type FormEvent } from 'react';
import { Plus, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { useMediaChannelOptions, useUpdateSettings } from '@/hooks/use-settings';
import { describeError } from '@/lib/api-client';
import { formatRelative, pluralize } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { AppSettings, MediaChannelOption } from '@/types/api';

/** Mirrors `isMediaChannelId` in @slc/shared: public or private channels, never a DM. */
const CHANNEL_ID = /^[CG][A-Z0-9]{2,20}$/;

/**
 * Reads a channel id out of what an administrator pastes: the id itself, or a Slack channel link
 * (`https://…slack.com/archives/C0123ABCD`), which is what "Copy link" in Slack gives them. Never
 * case-folded — Slack ids are upper-case, so a lower-case word is a channel name, not an id.
 */
export function parseChannelInput(value: string): string | undefined {
  const trimmed = value.trim();
  const candidate = /\/archives\/([A-Za-z0-9]+)/.exec(trimmed)?.[1] ?? trimmed;
  return CHANNEL_ID.test(candidate) ? candidate : undefined;
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const lookup = new Set(a);
  return b.every((value) => lookup.has(value));
}

/**
 * Which Slack channels images and videos are collected from. Opt-in per channel and empty by
 * default: a file is an employee's own content, not a pointer to something public, so collection
 * has to be a decision someone made for that channel.
 */
export function MediaChannelsCard({ settings }: { settings: AppSettings }) {
  const options = useMediaChannelOptions();
  const update = useUpdateSettings();
  const { toast } = useToast();

  const saved = settings.mediaChannelIds;
  const [selected, setSelected] = useState<string[]>(saved);
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);
  const manualId = useId();

  const changed = !sameSet(selected, saved);

  // Everything offered, plus anything chosen that no source has seen (added by id).
  const rows = useMemo<MediaChannelOption[]>(() => {
    const known = options.data ?? [];
    const knownIds = new Set(known.map((option) => option.value));
    const extra = selected
      .filter((id) => !knownIds.has(id))
      .map((id) => ({ value: id, label: id, monitored: false, mediaCount: 0, linkCount: 0 }));
    return [...known, ...extra];
  }, [options.data, selected]);

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) => row.label.toLowerCase().includes(needle) || row.value.toLowerCase().includes(needle),
    );
  }, [rows, search]);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const addManual = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const id = parseChannelInput(manual);
    if (!id) {
      setManualError('Paste a channel id such as C0123ABCD, or a link to the channel.');
      return;
    }
    setSelected((current) => (current.includes(id) ? current : [...current, id]));
    setManual('');
    setManualError(null);
  };

  const save = () => {
    update.mutate(
      { mediaChannelIds: [...selected].sort() },
      {
        onSuccess: (next) =>
          toast({
            title:
              next.mediaChannelIds.length === 0
                ? 'Media collection is off'
                : `Collecting images and videos from ${next.mediaChannelIds.length} ${pluralize(next.mediaChannelIds.length, 'channel')}`,
            description:
              next.mediaChannelIds.length === 0
                ? 'Nothing new will be stored. What was already collected stays on the Media page.'
                : 'Images and videos shared there from now on will appear on the Media page.',
            variant: 'success',
          }),
        onError: (error) =>
          toast({
            title: 'Could not save the media channels',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">Media collection</CardTitle>
          <Badge variant={saved.length > 0 ? 'success' : 'secondary'}>
            {saved.length > 0
              ? `${saved.length} ${pluralize(saved.length, 'channel')} monitored`
              : 'Off'}
          </Badge>
        </div>
        <CardDescription>
          Images and videos posted in the channels you pick are copied into SLC and shown on the
          Media page, credited to whoever posted them. Nothing is collected from any other
          conversation, and never from direct messages.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter channels…"
            aria-label="Filter channels"
            className="pl-8"
          />
        </div>

        {options.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load the channel list: {describeError(options.error)}
          </p>
        ) : options.isPending ? (
          <div className="flex flex-col gap-2" aria-label="Loading channels">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : visibleRows.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? 'SLC has not seen any channel yet. Channels appear here once a link, an image or a video is posted in one the bot is a member of — or add one by id below.'
              : 'No channel matches that filter.'}
          </p>
        ) : (
          <ul
            role="group"
            aria-label="Channels to collect images and videos from"
            className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border border-border"
          >
            {visibleRows.map((row) => {
              const id = `media-channel-${row.value}`;
              const checked = selected.includes(row.value);
              const facts = [
                row.mediaCount > 0
                  ? `${row.mediaCount.toLocaleString()} ${pluralize(row.mediaCount, 'file')}`
                  : null,
                row.linkCount > 0
                  ? `${row.linkCount.toLocaleString()} ${pluralize(row.linkCount, 'link')}`
                  : null,
                row.lastMediaAt ? `last upload ${formatRelative(row.lastMediaAt)}` : null,
              ].filter(Boolean);
              return (
                <li key={row.value}>
                  <label
                    htmlFor={id}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                      checked ? 'bg-primary/5' : 'hover:bg-accent/60',
                    )}
                  >
                    <Checkbox id={id} checked={checked} onCheckedChange={() => toggle(row.value)} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      {/* A channel no source has named yet shows its bare id. */}
                      <span
                        className={cn(
                          'truncate font-medium',
                          row.label === row.value && 'font-mono',
                        )}
                      >
                        {row.label}
                      </span>
                      {facts.length > 0 ? (
                        <span className="truncate text-xs text-muted-foreground">
                          {facts.join(' · ')}
                        </span>
                      ) : null}
                    </span>
                    {row.label !== row.value ? (
                      <span className="hidden shrink-0 font-mono text-xs text-muted-foreground sm:inline">
                        {row.value}
                      </span>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <form onSubmit={addManual} className="flex flex-col gap-1.5" noValidate>
          <Label htmlFor={manualId}>Add a channel that is not listed</Label>
          <div className="flex gap-2">
            <Input
              id={manualId}
              value={manual}
              onChange={(event) => {
                setManual(event.target.value);
                setManualError(null);
              }}
              placeholder="C0123ABCD or a link to the channel"
              aria-invalid={manualError !== null}
              aria-describedby={`${manualId}-hint`}
              autoComplete="off"
              spellCheck={false}
            />
            <Button type="submit" variant="outline">
              <Plus aria-hidden="true" className="h-4 w-4" />
              Add
            </Button>
          </div>
          <p
            id={`${manualId}-hint`}
            className={cn('text-xs', manualError ? 'text-destructive' : 'text-muted-foreground')}
          >
            {manualError ??
              'The SLC bot must be a member of the channel to see what is posted there.'}
          </p>
        </form>

        <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {selected.length === 0
              ? 'No channel selected: no images or videos will be collected.'
              : `${selected.length} ${pluralize(selected.length, 'channel')} selected.`}
          </p>
          <div className="flex gap-2 sm:shrink-0">
            {changed ? (
              <Button type="button" variant="ghost" onClick={() => setSelected(saved)}>
                Discard
              </Button>
            ) : null}
            <Button type="button" disabled={!changed} loading={update.isPending} onClick={save}>
              Save channels
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
